import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";
import { useLang } from "../context/LanguageContext";
import { EASE } from "./Reveal";

const NAV = [
  { key: "home", to: "/" },
  { key: "cars", to: "/cars" },
  { key: "dealers", to: "/dealers" },
  { key: "private", to: "/private-clients" },
  { key: "sell", to: "/sell-your-car" },
  { key: "process", to: "/our-process" },
  { key: "network", to: "/global-network" },
  { key: "about", to: "/about" },
  { key: "contact", to: "/contact" },
];

function LangSelector({ className = "" }) {
  const { lang, setLang, langs } = useLang();
  return (
    <div className={`flex items-center ${className}`} data-testid="language-selector">
      {langs.map((l, i) => (
        <span key={l.code} className="flex items-center">
          {i > 0 && <span className="text-[#333] text-[11px] mx-1">/</span>}
          <button
            data-testid={`lang-switcher-${l.code.toLowerCase()}`}
            onClick={() => setLang(l.code)}
            className={`px-1 py-1 transition-colors duration-300 whitespace-nowrap ${l.native === "ar" ? "lang-native-ar" : "mono text-[12px]"} ${lang === l.code ? "text-[#F3F3F0]" : "text-[#666663] hover:text-[#999995]"}`}
          >
            {l.label}
          </button>
        </span>
      ))}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#050607]/85 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
        data-testid="site-header"
      >
        <div className="grid grid-cols-[auto_1fr_auto] items-center px-5 md:px-10 h-[64px]">
          <Link to="/" onClick={goHome} data-testid="nav-logo-link" aria-label="VEYRONE home" className="p-3 -m-3 flex items-center">
            <Logo size={20} />
          </Link>

          <nav className="hidden xl:flex items-center justify-center gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.key}
                to={n.to}
                data-testid={`nav-link-${n.key}`}
                className={({ isActive }) => `link-line mono text-[10px] tracking-[0.16em] whitespace-nowrap ${isActive ? "active" : ""}`}
              >
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-3 md:gap-5">
            <LangSelector />
            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden flex flex-col justify-center items-end gap-[5px] w-10 h-10"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              <span className={`block h-px bg-[#F3F3F0] transition-all duration-400 ${open ? "w-6 rotate-45 translate-y-[3px]" : "w-6"}`} />
              <span className={`block h-px bg-[#F3F3F0] transition-all duration-400 ${open ? "w-6 -rotate-45 -translate-y-[3px]" : "w-4"}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-[#050607] xl:hidden flex flex-col justify-between pt-24 pb-10 px-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.6, ease: EASE }}
                  className="border-b border-white/10"
                >
                  <NavLink
                    to={n.to}
                    data-testid={`mobile-nav-${n.key}`}
                    className={({ isActive }) =>
                      `block py-4 font-display text-2xl ${isActive ? "text-[#F3F3F0]" : "text-[#999995]"}`
                    }
                  >
                    {t(`nav.${n.key}`)}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
